import { Plus, Trash2 } from "lucide-react";
import { useEffect } from "react";
import { IconButton } from "@doculegis/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  createEmptyEditorRow,
  type TemplateSectionEditorRow,
} from "./draft-template-sections";

type Props = {
  rows: TemplateSectionEditorRow[];
  onChange: (rows: TemplateSectionEditorRow[]) => void;
  disabled?: boolean;
  label?: string;
  idPrefix?: string;
};

export function DraftTemplateSectionList({
  rows,
  onChange,
  disabled,
  label = "Section headings",
  idPrefix = "dt-section",
}: Props) {
  useEffect(() => {
    if (rows.length === 0) {
      onChange([createEmptyEditorRow()]);
    }
  }, [rows.length, onChange]);

  function updateRow(id: string, title: string) {
    onChange(rows.map((row) => (row.id === id ? { ...row, title } : row)));
  }

  function removeRow(id: string) {
    const next = rows.filter((row) => row.id !== id);
    onChange(next.length === 0 ? [createEmptyEditorRow()] : next);
  }

  function addRow() {
    onChange([...rows, createEmptyEditorRow()]);
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <ol className="space-y-2">
        {rows.map((row, index) => (
          <li
            key={row.id}
            className="flex items-center gap-2 rounded-md border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-2 py-2 dark:border-white/10 dark:bg-slate-900/50"
          >
            <span
              className="w-6 shrink-0 text-center text-xs font-medium tabular-nums text-[var(--lm-muted)] dark:text-slate-400"
              aria-hidden
            >
              {index + 1}
            </span>
            <Input
              id={`${idPrefix}-${row.id}`}
              value={row.title}
              disabled={disabled}
              placeholder="Section title"
              onChange={(e) => updateRow(row.id, e.target.value)}
              aria-label={`Section ${index + 1} title`}
            />
            <IconButton
              type="button"
              size="sm"
              variant="destructive"
              title="Remove section"
              disabled={disabled}
              onClick={() => removeRow(row.id)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </IconButton>
          </li>
        ))}
      </ol>
      <Button
        type="button"
        variant="subtle"
        size="xs"
        disabled={disabled}
        onClick={addRow}
        className="mt-1"
      >
        <Plus className="h-3.5 w-3.5" />
        Add section
      </Button>
      <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">
        Add headings in the order they should appear in a new draft.
      </p>
    </div>
  );
}
